import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, History } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  listClients, createClient, updateClient, deleteClient, EMPTY_CLIENT,
} from './crmApi';
import ClientHistoryDialog from './ClientHistoryDialog';

const OPT_IN_OPTS = ['PENDING', 'GRANTED', 'DENIED'];

function toNum(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ClientForm({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const setCrit = (k, cast = (x) => x) => (e) => onChange({
    ...value, criteria: { ...(value.criteria || {}), [k]: cast(e.target.value) },
  });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label>Type</Label>
        <Select value={value.type} onValueChange={(v) => onChange({ ...value, type: v })}>
          <SelectTrigger data-testid="client-input-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="buyer">Buyer</SelectItem>
            <SelectItem value="seller">Seller</SelectItem>
            <SelectItem value="investor">Investor</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Opt-in status</Label>
        <Select value={value.twilio_opt_in_status} onValueChange={(v) => onChange({ ...value, twilio_opt_in_status: v })}>
          <SelectTrigger data-testid="client-input-optin"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OPT_IN_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="c-name">Name</Label>
        <Input id="c-name" data-testid="client-input-name" value={value.name} onChange={set('name')} />
      </div>
      <div>
        <Label htmlFor="c-email">Email</Label>
        <Input id="c-email" type="email" data-testid="client-input-email" value={value.email || ''} onChange={set('email')} />
      </div>
      <div>
        <Label htmlFor="c-phone">Phone</Label>
        <Input id="c-phone" data-testid="client-input-phone" placeholder="+15125550101" value={value.phone_number || ''} onChange={set('phone_number')} />
      </div>
      <div>
        <Label htmlFor="c-tag">Tag (blank = auto)</Label>
        <Input id="c-tag" data-testid="client-input-tag" value={value.tag || ''} onChange={set('tag')} />
      </div>

      {value.type === 'buyer' && (
        <>
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wide text-brand-green/60 mt-2 mb-1">Buyer criteria</div>
          </div>
          <div>
            <Label>Min price</Label>
            <Input type="number" data-testid="client-input-minprice" value={value.criteria?.min_price ?? ''} onChange={setCrit('min_price', toNum)} />
          </div>
          <div>
            <Label>Max price</Label>
            <Input type="number" data-testid="client-input-maxprice" value={value.criteria?.max_price ?? ''} onChange={setCrit('max_price', toNum)} />
          </div>
          <div>
            <Label>Min beds</Label>
            <Input type="number" data-testid="client-input-minbeds" value={value.criteria?.min_beds ?? ''} onChange={setCrit('min_beds', toNum)} />
          </div>
          <div>
            <Label>Min baths</Label>
            <Input type="number" step="0.5" data-testid="client-input-minbaths" value={value.criteria?.min_baths ?? ''} onChange={setCrit('min_baths', toNum)} />
          </div>
          <div>
            <Label>Min sqft</Label>
            <Input type="number" data-testid="client-input-minsqft" value={value.criteria?.min_sqft ?? ''} onChange={setCrit('min_sqft', toNum)} />
          </div>
          <div>
            <Label>Stories</Label>
            <Input data-testid="client-input-stories" value={value.criteria?.stories ?? 'any'} onChange={setCrit('stories')} />
          </div>
          <div className="sm:col-span-2">
            <Label>Target areas (comma-separated)</Label>
            <Input data-testid="client-input-areas"
                   value={(value.criteria?.target_areas || []).join(', ')}
                   onChange={(e) => onChange({
                     ...value,
                     criteria: {
                       ...(value.criteria || {}),
                       target_areas: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                     },
                   })} />
          </div>
        </>
      )}

      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox id="c-pre" data-testid="client-input-preapproved"
                  checked={!!value.pre_approved}
                  onCheckedChange={(v) => onChange({ ...value, pre_approved: !!v })} />
        <Label htmlFor="c-pre" className="text-sm">Pre-approved (buyer)</Label>
      </div>
      <div className="sm:col-span-2">
        <Label>Notes</Label>
        <Textarea rows={3} data-testid="client-input-notes" value={value.qualification_notes || ''}
                  onChange={set('qualification_notes')} />
      </div>
    </div>
  );
}

const OPT_IN_PILL_CLASS = {
  GRANTED: 'bg-brand-green text-brand-cream',
  DENIED: 'bg-red-100 text-red-700',
  PENDING: 'bg-yellow-100 text-yellow-800',
};

const TYPE_META = {
  buyer:    { label: 'Buyers',    color: '#059669', bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-500' },
  seller:   { label: 'Sellers',   color: '#B8860B', bg: 'bg-amber-100',   text: 'text-amber-800',   border: 'border-amber-500' },
  investor: { label: 'Investors', color: '#7C3AED', bg: 'bg-violet-100',  text: 'text-violet-800',  border: 'border-violet-500' },
  other:    { label: 'Other',     color: '#6B7280', bg: 'bg-gray-100',    text: 'text-gray-700',    border: 'border-gray-400' },
};
const TYPE_ORDER = ['buyer', 'seller', 'investor', 'other'];

function ClientsBody({ loading, rows, onEdit, onDelete, onHistory }) {
  if (loading) return <p className="text-brand-green/60">Loading clients…</p>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <p className="text-brand-green/70">No clients match those filters.</p>
      </div>
    );
  }
  const grouped = {};
  rows.forEach((c) => {
    const key = (c.type === 'buyer' || c.type === 'seller' || c.type === 'investor')
      ? c.type : 'other';
    (grouped[key] ||= []).push(c);
  });
  return (
    <div className="space-y-6">
      {TYPE_ORDER.filter((k) => grouped[k]?.length).map((key) => {
        const meta = TYPE_META[key];
        const list = grouped[key];
        return (
          <section key={key} data-testid={`clients-group-${key}`}>
            <header className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}
                    data-testid={`clients-group-pill-${key}`}>
                {meta.label} · {list.length}
              </span>
            </header>
            <div className={`bg-white rounded-xl overflow-x-auto shadow-sm border-l-4 ${meta.border}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60 border-b border-brand-cream">
                    <th className="p-3">Name</th>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Opt-in</th>
                    <th className="p-3">Tag</th>
                    <th className="p-3">Interested</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => {
                    const interestedCount = (c.interested_listings || []).length;
                    return (
                      <tr key={c.id} className="border-b border-brand-cream last:border-0" data-testid={`client-row-${c.id}`}>
                        <td className="p-3 font-medium text-brand-green">{c.name}</td>
                        <td className="p-3 text-brand-green/70">{c.phone_number || c.email || '—'}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${OPT_IN_PILL_CLASS[c.twilio_opt_in_status] || OPT_IN_PILL_CLASS.PENDING}`}>
                            {c.twilio_opt_in_status}
                          </span>
                        </td>
                        <td className="p-3 text-brand-green/70">{c.tag || '—'}</td>
                        <td className="p-3 text-brand-green/60 text-xs" data-testid={`client-interested-count-${c.id}`}>
                          {interestedCount > 0 ? `${interestedCount} propert${interestedCount === 1 ? 'y' : 'ies'}` : '—'}
                        </td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost" onClick={() => onHistory(c)}
                                  title="View history"
                                  data-testid={`client-history-${c.id}`}>
                            <History className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onEdit(c)} data-testid={`client-edit-${c.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onDelete(c)} data-testid={`client-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default function CrmClients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', status: '', tag: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [busy, setBusy] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);
  const { toast } = useToast();

  const params = useMemo(() => {
    const p = {};
    if (filters.type) p.type = filters.type;
    if (filters.status) p.status = filters.status;
    if (filters.tag) p.tag = filters.tag;
    return p;
  }, [filters]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await listClients(params)); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_CLIENT); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY_CLIENT, ...c, criteria: { ...EMPTY_CLIENT.criteria, ...(c.criteria || {}) } }); setShowForm(true); };

  const save = async () => {
    setBusy(true);
    try {
      // Backend rejects empty-string email (Pydantic EmailStr); send null instead.
      const body = { ...form, email: form.email || null, tag: form.tag || null };
      if (editing) { await updateClient(editing.id, body); toast({ title: 'Client updated' }); }
      else { await createClient(body); toast({ title: 'Client created' }); }
      setShowForm(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await deleteClient(c.id); toast({ title: 'Client deleted' }); await refresh(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  return (
    <div data-testid="crm-clients">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-brand-green">Clients</h2>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="client-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New client
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="client-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit client' : 'New client'}</DialogTitle>
              <DialogDescription>
                Contact details plus buyer criteria. Leave "Tag" blank to have it auto-assigned (Hot Buyer / Cold Lead / Seller).
              </DialogDescription>
            </DialogHeader>
            <ClientForm value={form} onChange={setForm} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !form.name} data-testid="client-save-btn">
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={filters.type || 'all'} onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-36" data-testid="clients-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="buyer">Buyer</SelectItem>
              <SelectItem value="seller">Seller</SelectItem>
              <SelectItem value="investor">Investor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Opt-in</Label>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-36" data-testid="clients-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {OPT_IN_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Tag</Label>
          <Input className="w-40" value={filters.tag} data-testid="clients-filter-tag"
                 onChange={(e) => setFilters({ ...filters, tag: e.target.value })} placeholder="Any" />
        </div>
        {(filters.type || filters.status || filters.tag) && (
          <Button variant="ghost" onClick={() => setFilters({ type: '', status: '', tag: '' })} data-testid="clients-filter-clear">
            Clear
          </Button>
        )}
      </div>

      <ClientsBody loading={loading} rows={rows} onEdit={openEdit} onDelete={remove}
                   onHistory={(c) => setHistoryClient(c)} />

      <ClientHistoryDialog
        clientId={historyClient?.id}
        clientName={historyClient?.name}
        open={!!historyClient}
        onOpenChange={(v) => { if (!v) setHistoryClient(null); }}
      />
    </div>
  );
}
